import React, { useEffect } from "react";
import { useImmer } from "use-immer";
import { v4 } from "uuid";

import { WORKFLOW_IDS } from "@intelligems/ig-plugin/utils/workflow-ids";
import { AiNodeContext } from "@intelligems/ig-types/schemas/ai/context-schemas";
import { ChatWorkflowRequestBodyDto } from "@intelligems/ig-types/dtos/ai/workflows/chat-workflow";
import { UiStreamAction } from "@intelligems/ig-types/schemas/ai/ui-stream-actions";
import Grid from "@mui/material/Grid";
import { useUiActionWorkflow } from "@src/api/ai/execute-workflows/ui-action-workflow";
import { useGetOrg } from "@src/api/organizations/get-organization";
import { useThemeTemplates } from "@src/api/shopify-themes/get-theme-templates";
import { useLiveOrgTheme } from "@src/api/shopify-themes/get-themes";
import { useIsUnderOfferLimit } from "@src/features/sharedExperience/hooks/use-is-under-offer-limit";
import { useIsMerchandisingGated } from "@src/features/sharedExperience/hooks/use-merchandising-plan-access";
import { useIsAgenticLogicEnabled } from "@src/hooks/feature-flags/useIsAgenticLogicEnabled";
import { useGetShopDomain } from "@src/api/shops/get-domain";
import { ExecutionPanel } from "@src/features/ai/components/WorkflowEditor/execution/ExecutionPanel";
import {
  ExecutionPanelModelOption,
  OnAiChatSend,
} from "@src/features/ai/components/WorkflowEditor/execution/types";
import { useDispatch, useSelector } from "@src/store";
import { getAiChat, handleAiChatAction } from "@src/store/slices/aiChat";
import { getAiChatProps } from "@src/store/slices/global";

import { useStopChat } from "./hooks/useStopChat";
import { useUiActionsChatState } from "./hooks/useUiActionsChatState";
import { useWorkflowSelectors } from "./hooks/useWorkflowSelectors";
import {
  buildWorkflowVariables,
  createInitialAssistantMessage,
  createUserMessage,
} from "./utils/workflowUtils";
import { RateConversation } from "@src/features/ai/components/WorkflowEditor/execution/components/RateConversation";

interface UiActionsChatProps {
  onClose?: () => void;
}

// Create initial message once at module load time
const initialAssistantMessage = createInitialAssistantMessage();

const MODEL_OPTIONS: ExecutionPanelModelOption[] = [
  { name: "GPT 5.6 Sol", id: "openai:gpt-5.6-sol" },
  { name: "Gemini 2.5 Flash", id: "google:gemini-2.5-flash" },
  {
    name: "Claude 4 Sonnet",
    id: "anthropic:claude-4-sonnet-20250514",
  },
];

export const UiActionsChat = ({ onClose }: UiActionsChatProps) => {
  const { initialContext } = useSelector(getAiChatProps<AiNodeContext>);
  const dispatch = useDispatch();
  const domain = useGetShopDomain();
  const org = useGetOrg();

  const [model, setModel] = useImmer<string | undefined>(MODEL_OPTIONS[0].id);

  // Use custom hooks for state management
  const { panelRef, workflowState, setShowRating, setRestartFromFirstNode } =
    useUiActionsChatState();

  const {
    variations,
    experience,
    redirects,
    audience,
    experienceKeyMetrics,
    pageTargeting,
    onsiteInjections,
    analyticsProducts,
    offers,
    checkoutBlocks,
  } = useWorkflowSelectors();

  // API hooks
  const liveThemeId = useLiveOrgTheme();
  const isUnderOfferLimit = useIsUnderOfferLimit();
  const isMerchandisingGated = useIsMerchandisingGated();
  const isAgenticLogicEnabled = useIsAgenticLogicEnabled();
  const themeTemplates = useThemeTemplates(liveThemeId);
  const aiChat = useSelector(getAiChat);

  const { chatWorkflow } = useUiActionWorkflow(setRestartFromFirstNode);
  const stopChat = useStopChat(chatWorkflow, aiChat.sessionId);

  useEffect(() => {
    const metadataModel =
      typeof initialContext?.metadata?.model === "string"
        ? initialContext.metadata.model
        : undefined;

    if (metadataModel) {
      setModel(metadataModel);
    }
  }, [initialContext?.metadata?.model, setModel]);

  const onSend: OnAiChatSend = {
    chat: (payload: ChatWorkflowRequestBodyDto) => {
      const variables = buildWorkflowVariables({
        experience,
        domain: domain.data,
        orgId: org.data.org.id,
        shopId: org.data.org.shopId,
        variations,
        redirects,
        onsiteInjections,
        audience,
        experienceKeyMetrics,
        themeTemplates: themeTemplates?.data?.templates || [],
        pageTargeting,
        analyticsProducts,
        started: !!aiChat.sessionId,
        isUnderOfferLimit,
        isMerchandisingGated,
        isAgenticLogicEnabled,
        offers,
        experienceStartedAt: experience.startedAtTs,
        checkoutBlocks,
      });

      setShowRating(true);

      // Stable id shared between the optimistic user bubble and the server
      // echo so the reducer reconciles by id instead of appending a duplicate.
      const inputMessageId = v4();
      const { message } = createUserMessage(payload.input);
      if (message) {
        message.id = inputMessageId;
      }
      const nodeId = v4();
      const turnId = v4();

      if (!aiChat.sessionId) {
        const sessionId = v4();

        const actions: UiStreamAction[] = [
          {
            type: "initialize",
            workflowId: WORKFLOW_IDS.UI_ACTIONS,
            sessionId,
            nodeId,
          },
        ];

        if (message) {
          actions.push({
            type: "addMessage",
            turnId,
            message,
            nodeId,
            nodeName: "User Input",
          });
        }
        dispatch(handleAiChatAction(actions));

        void chatWorkflow.mutate({
          initialContext: {
            ...initialContext,
            variables,
            sessionId,
            messages: [initialAssistantMessage, message],
          },
          sessionId,
          workflowId: WORKFLOW_IDS.UI_ACTIONS,
          input: payload.input,
          inputMessageId,
          model: model || undefined,
        });
      } else {
        if (message) {
          dispatch(
            handleAiChatAction([
              {
                type: "addMessage",
                turnId,
                message,
                nodeId,
                nodeName: "User Input",
              },
            ])
          );
        }

        void chatWorkflow.mutate({
          sessionId: payload.sessionId || aiChat.sessionId,
          input: payload.input,
          inputMessageId,
          variables,
          restartFromFirstNode: workflowState.restartFromFirstNode,
          model,
        });
        setRestartFromFirstNode(true);
      }
    },
    stop: stopChat,
    isPending: chatWorkflow.isPending,
  };

  return (
    <Grid
      container
      direction={"column"}
      wrap={"nowrap"}
      spacing={2}
      sx={{ height: "100%" }}
    >
      <Grid sx={{ flexGrow: 1, minHeight: 0 }}>
        <ExecutionPanel
          feature={"create_test"}
          ref={panelRef}
          onClose={onClose}
          onSend={onSend}
          workflowId={WORKFLOW_IDS.UI_ACTIONS}
          showSystemMessages={false}
          showToolCallOutput={false}
          showToolsAsTasks={true}
          clearReasoningOnMessage={true}
          showReasoningMessages={false}
          showConditionNodes={false}
          slots={{
            promptInputTools: workflowState.showRating ? (
              <RateConversation
                allowComments={true}
                metadata={{
                  model: model ?? "",
                  experienceType: experience.type,
                  experienceName: experience.name,
                  experienceId: experience.id!,
                }}
              />
            ) : null,
          }}
        />
      </Grid>
    </Grid>
  );
};
